import { useState, useEffect, useCallback, useRef } from 'react';
import { getCurrentLanguage } from '@/lib/i18n';
import { API_BASE } from '@/lib/api';
import { traceApiError, traceApiResponse } from '@/lib/api-tracing';

interface GeoJSONFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  properties: {
    id: string;
    type: string;
    fadingOut?: boolean; // Added for fade-out effect
  };
}

interface GeoJSONResponse {
  places: GeoJSONFeature[];
  pagination: {
    has_next: boolean;
    has_prev: boolean;
    next_cursor?: string;
    prev_cursor?: string;
  };
  bounds?: {
    westLng: number;
    southLat: number;
    eastLng: number;
    northLat: number;
  };
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface UsePlacesMapOptions {
  propertyId: string;
  placeType?: string;
  bounds?: MapBounds;
  propertyLat?: number;
  propertyLon?: number;
  maxCacheAge?: number; // Cache age in milliseconds (default: 5 minutes)
  viewportMargin?: number; // Margin around viewport in degrees (default: 0.1)
}

interface UsePlacesMapReturn {
  features: GeoJSONFeature[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  reset: () => void;
  manualLoadMore: () => void;
}

interface PlaceProperties {
  place_id?: string;
  place_type?: string;
  id?: string;
  type?: string;
  [key: string]: unknown;
}

export const usePlacesMap = ({ 
  propertyId, 
  placeType, 
  bounds, 
  propertyLat, 
  propertyLon,
  maxCacheAge = 5 * 60 * 1000, // 5 minutes default
  viewportMargin = 0.1 // 0.1 degrees default
}: UsePlacesMapOptions): UsePlacesMapReturn => {
  const [features, setFeatures] = useState<GeoJSONFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const loadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const reachedEndRef = useRef(false);
  const currentBoundsRef = useRef<MapBounds | undefined>(bounds);
  const pendingRequestRef = useRef<string | null>(null);

  // Cache management
  const cacheRef = useRef<Map<string, {
    features: GeoJSONFeature[];
    timestamp: number;
    bounds: MapBounds;
    hasMore: boolean;
    nextCursor?: string;
  }>>(new Map());

  // Cache key by rounded bounds and cursor
  const getCacheKey = useCallback((bounds: MapBounds, cursor?: string) => {
    const roundedBounds = {
      north: Math.round(bounds.north * 1000) / 1000,
      south: Math.round(bounds.south * 1000) / 1000,
      east: Math.round(bounds.east * 1000) / 1000,
      west: Math.round(bounds.west * 1000) / 1000,
    };
    return `${roundedBounds.north}-${roundedBounds.south}-${roundedBounds.east}-${roundedBounds.west}-${cursor || 'initial'}-${placeType || 'all'}`;
  }, [placeType]);

  const isPointInViewport = useCallback((point: GeoJSONFeature, viewportBounds: MapBounds) => {
    const [lon, lat] = point.geometry.coordinates;
    return lat >= viewportBounds.south - viewportMargin &&
           lat <= viewportBounds.north + viewportMargin &&
           lon >= viewportBounds.west - viewportMargin &&
           lon <= viewportBounds.east + viewportMargin;
  }, [viewportMargin]);

  // Cleanup cache periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const keysToDelete: string[] = [];
      cacheRef.current.forEach((cacheEntry, key) => {
        if (now - cacheEntry.timestamp > maxCacheAge) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => cacheRef.current.delete(key));
    }, 2 * 60 * 1000);
    return () => clearInterval(cleanupInterval);
  }, [maxCacheAge]);

  // Track out-of-bounds points and their eviction timers
  const evictionTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const outOfBoundsSince = useRef<Map<string, number>>(new Map()); // Track when a spot first goes out of bounds
  const [allFeatures, setAllFeatures] = useState<GeoJSONFeature[]>([]); // All loaded features, not just visible

  // Helper to check if a point is the property main point
  const isPropertyMainPoint = useCallback((feature: GeoJSONFeature) => {
    if (!propertyLat || !propertyLon) return false;
    const [lon, lat] = feature.geometry.coordinates;
    return (
      Math.abs(lat - propertyLat) < 1e-6 && Math.abs(lon - propertyLon) < 1e-6
    );
  }, [propertyLat, propertyLon]);

  // Add new features (deduplicated by ID, but allow re-adding if not present)
  const addFeatures = useCallback((newFeatures: GeoJSONFeature[]) => {
    setAllFeatures(prev => {
      // Only deduplicate against the current state
      const currentIds = new Set(prev.map(f => f.properties.id));
      const toAdd = newFeatures.filter(f => !currentIds.has(f.properties.id));
      const merged = [...prev, ...toAdd];
      return merged;
    });
  }, []);

  // Remove features by ID
  const removeFeaturesById = useCallback((ids: string[]) => {
    setAllFeatures(prev => prev.filter(f => !ids.includes(f.properties.id) || isPropertyMainPoint(f)));
  }, [isPropertyMainPoint]);

  // On bounds change, fetch all pages for the new bounds and add new points
  useEffect(() => {
    if (!bounds) return;
    // Start fresh pagination for new bounds
    setHasMore(true);
    setNextCursor(undefined);
    reachedEndRef.current = false;
    loadingRef.current = false;
    lastLoadTimeRef.current = 0;
    pendingRequestRef.current = null;
    // Fetch all pages for new bounds
    let cancelled = false;
    const fetchAllPages = async () => {
      let cursor: string | undefined = undefined;
      let keepGoing = true;
      while (keepGoing && !cancelled) {
        setLoading(true);
        const params = new URLSearchParams();
        if (cursor) params.append('cursor', cursor);
        if (placeType && placeType !== 'all') params.append('place_type', placeType);
        params.append('limit', '10');
        params.append('north', bounds.north.toString());
        params.append('south', bounds.south.toString());
        params.append('east', bounds.east.toString());
        params.append('west', bounds.west.toString());
        if (propertyLat !== undefined && propertyLon !== undefined) {
          params.append('lat', propertyLat.toString());
          params.append('lon', propertyLon.toString());
        }
        const url = `${API_BASE}/property/${propertyId}/places/map?${params.toString()}`;
        const startTime = performance.now();
        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Accept-Language': getCurrentLanguage(),
            },
          });
          if (!response.ok) {

            const duration = performance.now() - startTime;
            traceApiError(
              url,
              'GET',
              new Error(`HTTP ${response.status}: ${response.statusText}`),
              { propertyId, cursor, placeType, bounds, propertyLat, propertyLon, operation: 'fetch_places_map', duration }
            );

            console.error('API request failed:', {
              status: response.status,
              statusText: response.statusText,
              url: url
            });
            break;
          }
          const data: GeoJSONResponse = await response.json();

          const duration = performance.now() - startTime;
          traceApiResponse(
            url,
            'GET',
            response,
            data,
            duration,
            { propertyId, cursor, placeType, bounds, propertyLat, propertyLon, operation: 'fetch_places_map' }
          );

          if (!data.places || !Array.isArray(data.places)) {
            console.error('Invalid API response structure:', data);
            break;
          }
          // Normalize properties: id and type
          const geoJSONFeatures = data.places.map(f => {
            const props = f.properties as PlaceProperties;
            return {
              ...f,
              properties: {
                ...props,
                id: props.place_id ?? props.id,
                type: props.place_type ?? props.type,
              }
            };
          });
          addFeatures(geoJSONFeatures);
          if (!data.pagination.has_next) {
            keepGoing = false;
          } else {
            cursor = data.pagination.next_cursor || undefined;
          }
        } catch (err) {
          const duration = performance.now() - startTime;
          traceApiError(
            url,
            'GET',
            err as Error,
            { propertyId, cursor, placeType, bounds, propertyLat, propertyLon, operation: 'fetch_places_map', duration }
          );
          console.error('Error fetching places map:', err);
          break;
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAllPages();
    return () => { cancelled = true; };
  }, [bounds, placeType, propertyLat, propertyLon, propertyId, addFeatures]);

  // On every bounds change, start/stop eviction timers for out-of-bounds points
  useEffect(() => {
    if (!bounds) return;
    const now = Date.now();
    const visibleIds = new Set<string>();
    for (const f of allFeatures) {
      const [lon, lat] = f.geometry.coordinates;
      if (
        lat >= bounds.south - viewportMargin &&
        lat <= bounds.north + viewportMargin &&
        lon >= bounds.west - viewportMargin &&
        lon <= bounds.east + viewportMargin
      ) {
        visibleIds.add(f.properties.id);
        // Cancel eviction timer if point is back in bounds
        const timer = evictionTimers.current.get(f.properties.id);
        if (timer) {
          clearTimeout(timer);
          evictionTimers.current.delete(f.properties.id);
        }
        // Remove out-of-bounds tracking
        outOfBoundsSince.current.delete(f.properties.id);
      } else if (!isPropertyMainPoint(f)) {
        // Track when it first went out of bounds
        if (!outOfBoundsSince.current.has(f.properties.id)) {
          outOfBoundsSince.current.set(f.properties.id, now);
        }
        // Only start eviction timer if out of bounds for at least 5s
        if (!evictionTimers.current.has(f.properties.id)) {
          const outSince = outOfBoundsSince.current.get(f.properties.id) || now;
          const grace = 5000; // 5 seconds
          const timeOut = outSince + grace - now;
          if (timeOut <= 0) {
            // Already out for grace period, start eviction timer
            const timer = setTimeout(() => {
              evictionTimers.current.delete(f.properties.id);
              outOfBoundsSince.current.delete(f.properties.id);
              removeFeaturesById([f.properties.id]);
            }, 60000); // 60 seconds
            evictionTimers.current.set(f.properties.id, timer);
          } else {
            // Wait for grace period, then start eviction timer
            const graceTimer = setTimeout(() => {
              // Double check still out of bounds
              if (!visibleIds.has(f.properties.id)) {
                const timer = setTimeout(() => {
                  evictionTimers.current.delete(f.properties.id);
                  outOfBoundsSince.current.delete(f.properties.id);
                  removeFeaturesById([f.properties.id]);
                }, 60000); // 60 seconds
                evictionTimers.current.set(f.properties.id, timer);
              }
            }, timeOut);
            evictionTimers.current.set(f.properties.id, graceTimer);
          }
        }
      }
    }
    // Clean up timers for points that are now visible
    for (const [id, timer] of evictionTimers.current.entries()) {
      if (visibleIds.has(id)) {
        clearTimeout(timer);
        evictionTimers.current.delete(id);
      }
    }
  }, [bounds, allFeatures, viewportMargin, isPropertyMainPoint, removeFeaturesById]);

  // Only show features in viewport (for rendering)
  const [visibleFeatures, setVisibleFeatures] = useState<GeoJSONFeature[]>([]);
  useEffect(() => {
    if (!bounds || allFeatures.length === 0) {
      setVisibleFeatures(allFeatures);
      return;
    }
    const now = Date.now();
    const filteredFeatures = allFeatures.map(feature => {
      const [lon, lat] = feature.geometry.coordinates;
      const inViewport =
        lat >= bounds.south - viewportMargin &&
        lat <= bounds.north + viewportMargin &&
        lon >= bounds.west - viewportMargin &&
        lon <= bounds.east + viewportMargin;
      // Mark as fadingOut if out of bounds and has a timer or grace
      let fadingOut = false;
      if (!inViewport && !isPropertyMainPoint(feature)) {
        if (evictionTimers.current.has(feature.properties.id) || outOfBoundsSince.current.has(feature.properties.id)) {
          fadingOut = true;
        }
      }
      return {
        ...feature,
        properties: {
          ...feature.properties,
          fadingOut,
        },
      };
    }).filter(feature => {
      // Only show features in viewport or in grace/eviction period
      const [lon, lat] = feature.geometry.coordinates;
      const inViewport =
        lat >= bounds.south - viewportMargin &&
        lat <= bounds.north + viewportMargin &&
        lon >= bounds.west - viewportMargin &&
        lon <= bounds.east + viewportMargin;
      return inViewport || feature.properties.fadingOut;
    });
    // --- CATEGORY FILTERING ---
    let categoryFiltered = filteredFeatures;
    if (placeType && placeType !== 'all') {
      categoryFiltered = filteredFeatures.filter(f => f.properties.type === placeType);
    }
    setVisibleFeatures(categoryFiltered);
  }, [bounds, allFeatures, viewportMargin, isPropertyMainPoint, placeType]);

  // Manual trigger for testing
  const manualLoadMore = useCallback(() => {}, []); // No-op, auto-loads

  return {
    features: visibleFeatures,
    loading,
    hasMore,
    loadMore: () => {}, // No-op, auto-loads
    refetch: () => {}, // No-op, auto-loads
    reset: () => {}, // No-op, auto-loads
    manualLoadMore,
  };
}; 